import { useUser } from '../../../common/hooks/auth/useUser'
import { useUserApi } from '../../../common/api-context'
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form'
import IconBox from '../../../components/common/icon-box'
import { useTranslation } from '../../../useTranslation'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/free-solid-svg-icons'
import Button from '../../../components/common/button'
import { useMutation } from '@tanstack/react-query'
import { UpdateCorrespondenceEmailsDto } from '@ringo/shared'
import { toast } from 'react-toastify'
import ControlledInputText from '../../../components/form/controlled-input-text'
import { useValidationRules } from '../../../common/validation-rules'
import { Validation } from '@ringo/types'
import { array, object } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

type CorrespondenceFormState = { emails: { email: string }[] }

export const CorrespondenceEmails: React.FC = () => {
    const userApi = useUserApi()
    const validationRules = useValidationRules()
    const { t } = useTranslation()
    const { user, refreshUserData } = useUser()

    const validationObject: Validation<CorrespondenceFormState> = {
        emails: array()
            .max(4)
            .of(
                object().shape({
                    email: validationRules.email,
                })
            ),
    }

    const schema = object(validationObject)

    const { handleSubmit, control } = useForm<CorrespondenceFormState>({
        resolver: yupResolver(schema as any),
        defaultValues: {
            emails:
                user?.correspondenceEmails?.map((email) => ({
                    email,
                })) || [],
        },
    })

    const { fields: emails, ...operations } = useFieldArray({
        name: 'emails',
        control,
    })

    const mutation = useMutation({
        mutationFn: async (formData: UpdateCorrespondenceEmailsDto) => userApi.updateCorrespondenceEmails(formData),
        onSuccess: () => {
            refreshUserData()
            toast.success(t('myAccount.personalData.correspondenceEmailsSuccessfullyUpdated'))
        },
    })

    const updateCorrespondenceEmails: SubmitHandler<CorrespondenceFormState> = async (data) => {
        mutation.mutate({ correspondenceEmails: data.emails.map((x) => x.email) as any })
    }

    return (
        <IconBox icon={'envelopes-bulk'}>
            <div className="pt-12 pb-6 px-2 max-tablet:pt-10">
                <p className="font-bold text-xl text-left leading-none max-tablet:text-sm">{t('myAccount.personalData.correspondenceEmailsDescription')}</p>

                <div className="space-y-3 pt-3">
                    {emails.map((email, index) => (
                        <div key={email.id} className="flex items-center gap-4">
                            <ControlledInputText
                                label=""
                                hideLabel
                                placeholder={t('email')}
                                name={`emails.${index}.email`}
                                control={control}
                                wrapperClassName="grow"
                            />

                            <FontAwesomeIcon className="cursor-pointer" icon={faTrash} onClick={() => operations.remove(index)} />
                        </div>
                    ))}
                </div>

                {emails.length < 4 && (
                    <div
                        className="mt-2 p-[10px] bg-[#ededed] mx-auto w-fit cursor-pointer text-base font-bold leading-none text-black hover:bg-[#dedede] max-tablet:w-full max-tablet:text-center max-tablet:text-sm"
                        onClick={() => operations.append({ email: '' })}
                    >
                        {t('myAccount.personalData.addEmail')}
                    </div>
                )}

                <div className="flex justify-center pt-10">
                    <Button type="squareBlue" icon="check" text={t('save')} isLoading={mutation.isPending} onClick={handleSubmit(updateCorrespondenceEmails)} />
                </div>
            </div>
        </IconBox>
    )
}
